import { useCallback, useMemo, useRef, useState } from "react";
import type { ClockReading } from "@infoflyt/shared/timecode";
import { Wheel } from "../components/Wheel";
import { LiquidGlass } from "../components/LiquidGlass";
import { useSession } from "../state/SessionProvider";
import { confirm as hapticConfirm, heavy } from "../components/haptics";
import type { TimeEntry } from "../state/types";

/** Terskelen dagen regnes som lang over. Se docs/07-modul-timefoering.md. */
const NORMAL_DAY_MIN = 8 * 60;

const p2 = (n: number): string => String(n).padStart(2, "0");
const hm = (min: number): string => {
  const m = ((Math.round(min) % 1440) + 1440) % 1440;
  return `${p2(Math.floor(m / 60))}:${p2(m % 60)}`;
};
const dur = (min: number): string => {
  const m = Math.max(0, Math.round(min));
  return `${Math.floor(m / 60)}:${p2(m % 60)}`;
};
/** Spenn over midnatt: en kveldsvakt slutter dagen etter. */
const span = (from: number, to: number): number => (to - from < 0 ? to - from + 1440 : to - from);

/**
 * Varighet i minutter mellom to tidsstempler.
 *
 * Regnes fra millisekundene som faktisk er lagret, ikke fra minutt-paa-doegnet.
 * `span` ruller rundt ved 1440, saa et skift paa 24 timer eller mer kom ut som
 * null — og datoen forsvant, saa to oekter paa hver sin dag kunne se like ut.
 */
const varighet = (fraMs: number, tilMs: number): number =>
  Math.max(0, Math.round((tilMs - fraMs) / 60_000));

const minutesOfDay = (ms: number): number => {
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes();
};

/** Setter klokkeslett på en gitt dato, og ruller til neste dag om nødvendig. */
function atMinutes(baseMs: number, minutes: number, mustBeAfter?: number): number {
  const d = new Date(baseMs);
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  let out = d.getTime();
  if (mustBeAfter !== undefined && out < mustBeAfter) out += 86_400_000;
  return out;
}

const dayLabel = (ms: number): string => {
  const days = ["Søndag", "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "Lørdag"];
  const d = new Date(ms);
  return `${days[d.getDay()] ?? ""} ${p2(d.getDate())}.${p2(d.getMonth() + 1)}`;
};

type Sheet =
  | { kind: "none" }
  /** Sett tid inn eller ut med hjul. */
  | { kind: "time"; field: "start" | "end"; h: number; m: number }
  /** Dagen er over åtte timer og må begrunnes før den lagres. */
  | { kind: "overtime" }
  | { kind: "clear" }
  | { kind: "detail"; id: string };

/**
 * Timer-skjermen.
 *
 * Tre faser: ikke stemplet inn, på jobb, og i ferd med å avslutte. Hjulet
 * øverst viser klokkeslettet som gjelder for fasen og kan dras for å sette
 * en annen tid — regi stempler ikke alltid i det øyeblikket hen begynner.
 *
 * Bygget etter `Timer faser.dc.html`. Overtidsbegrunnelsen er et krav fra
 * designet, ikke en beregning mot overenskomst: appen regner ingen satser og
 * tar ikke stilling til hva timene er verdt. Se docs/07-modul-timefoering.md.
 */
export function TimerScreen({ now }: { now: ClockReading }): JSX.Element {
  const nowUtcMs = now.wallUtcMs;
  const {
    timeEntries, startTimer, stopTimer, deleteTimeEntry, clearTimeEntries,
    timerAnnetSted, produksjoner,
  } = useSession();

  /*
   * En arbeidsdag som gaar i en annen produksjon.
   *
   * Det gaar én timer per person: man kan ikke jobbe paa to produksjoner i
   * samme oeyeblikk. `startTimer` nekter naar denne er satt, og da MAA
   * flaten si hvor dagen gaar — en knapp som ikke gjoer noe er verre enn
   * ingen knapp.
   */
  const laast = timerAnnetSted !== null;
  const laastHos = produksjoner
    .find((p) => p.production.id === timerAnnetSted?.productionId)
    ?.production.name ?? "en annen produksjon";

  const open = useMemo(
    () => timeEntries.find((t) => t.stoppedAtUtcMs === null) ?? null,
    [timeEntries],
  );
  const logged = useMemo(
    () => timeEntries.filter((t) => t.stoppedAtUtcMs !== null),
    [timeEntries],
  );

  /** Sluttid valgt, men dagen ennå ikke lagret. */
  const [pendingEnd, setPendingEnd] = useState<number | null>(null);
  /** Starttid valgt før man har stemplet inn. */
  const [pendingStart, setPendingStart] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [sheet, setSheet] = useState<Sheet>({ kind: "none" });
  const [wheel, setWheel] = useState<0 | 1>(1);

  const nowMin = minutesOfDay(nowUtcMs);
  /** Sann naar regi selv har satt sluttiden i hjulet. Se saveDay. */
  const endEdited = useRef(false);
  const phase: "idle" | "working" | "ending" =
    open === null ? "idle" : pendingEnd === null ? "working" : "ending";

  const startMin = open === null ? null : minutesOfDay(open.startedAtUtcMs);
  const endLive = pendingEnd ?? nowMin;
  /*
   * Forløpt tid regnes på monoton klokke.
   *
   * Operativsystemet justerer veggklokka uten varsel, og et hopp midt i
   * skiftet ville flyttet tallet regi fører timer etter. Økten lagrer sin
   * egen monotone avlesning ved start, og så lenge telefonen ikke er startet
   * om siden da, er differansen eksakt.
   *
   * Faller tilbake på veggklokka når det ikke finnes noe å telle fra: økter
   * lagret før feltene fantes, starttider satt for hånd, og etter en
   * omstart. Da regnes det fra millisekundene som er lagret — ikke fra
   * minutt-på-døgnet, som ruller rundt ved 1440 og ga null på et skift over
   * 24 timer.
   *
   * Har regi selv valgt sluttidspunkt i hjulet, gjelder valget. Det er en
   * oppgitt verdi, ikke en måling.
   */
  const worked = open === null
    ? 0
    : pendingEnd !== null
      ? span(minutesOfDay(open.startedAtUtcMs), pendingEnd)
      : open.monotonicMs !== null && open.bootId === now.bootId
        ? Math.max(0, Math.round((now.monotonicMs - open.monotonicMs) / 60_000))
        : varighet(open.startedAtUtcMs, nowUtcMs);
  const over = Math.max(0, worked - NORMAL_DAY_MIN);

  // Hjulet viser fasens klokkeslett: valgt start, valgt slutt, eller nå.
  const shown = phase === "idle"
    ? pendingStart ?? nowMin
    : phase === "working"
      ? nowMin
      : endLive;
  const shownH = Math.floor(shown / 60);
  const shownM = shown % 60;

  const bump = useCallback((unit: 0 | 1, dir: 1 | -1): void => {
    setWheel(unit);
    const apply = (v: number): number => {
      const h = Math.floor(v / 60);
      const m = v % 60;
      return unit === 0
        ? (((h + dir) % 24 + 24) % 24) * 60 + m
        : h * 60 + ((m + dir) % 60 + 60) % 60;
    };
    if (phase === "idle") setPendingStart((v) => apply(v ?? nowMin));
    else { endEdited.current = true; setPendingEnd((v) => apply(v ?? nowMin)); }
  }, [phase, nowMin]);

  const saveDay = useCallback((withNote: string): void => {
    if (open === null || pendingEnd === null) return;
    heavy();
    /*
     * Stopper paa naa naar sluttiden ikke er valgt for haand.
     *
     * atMinutes legger et klokkeslett paa startdatoen og ruller én gang om
     * det havner foer starten. Glemte regi aa stemple ut og stoppet neste
     * kveld, landet 25 timer paa startdatoen og ble lagret som én — og
     * overtidsvarselet kom aldri. Har regi valgt tiden selv i hjulet,
     * gjelder valget.
     */
    const stopp = endEdited.current
      ? atMinutes(open.startedAtUtcMs, pendingEnd, open.startedAtUtcMs)
      : nowUtcMs;
    stopTimer(stopp, withNote);
    endEdited.current = false;
    setPendingEnd(null);
    setNote("");
    setSheet({ kind: "none" });
  }, [open, pendingEnd, nowUtcMs, stopTimer]);

  const action = useCallback((): void => {
    hapticConfirm();
    if (phase === "idle") {
      startTimer(pendingStart === null ? undefined : atMinutes(nowUtcMs, pendingStart));
      setPendingStart(null);
      return;
    }
    if (phase === "working") {
      // «Ferdig paa jobb» betyr naa. Et tidligere valg i hjulet gjelder ikke.
      endEdited.current = false;
      setPendingEnd(nowMin);
      return;
    }
    // Dagen er over åtte timer. Skriv kort hvorfor.
    if (over > 0) { setSheet({ kind: "overtime" }); return; }
    saveDay("");
  }, [phase, pendingStart, nowUtcMs, nowMin, over, startTimer, saveDay]);

  const actionLabel = laast
    ? `Dagen går på ${laastHos}`
    : phase === "idle"
    ? pendingStart === null ? "Start på jobb" : `Start på jobb ${hm(pendingStart)}`
    : phase === "working"
      ? "Ferdig på jobb"
      : "Lagre dagen";

  const sub = laast
    ? "bytt produksjon i menyen for å avslutte den"
    : phase === "idle"
    ? pendingStart === null
      ? "ikke stemplet inn · dra tallet for å sette tiden selv"
      : "valgt starttid · dra tallet for å endre"
    : phase === "working"
      ? "klokka nå · dra tallet for å sette en annen sluttid"
      : "sluttid · dra tallet for å endre før du lagrer";

  const detail = sheet.kind === "detail"
    ? logged.find((t) => t.id === sheet.id) ?? null
    : null;

  const sheetOpen = sheet.kind !== "none";

  return (
    <div className="surface" style={{ height: "100%", display: "flex", position: "relative" }}>
      <div className="timer">
        <div className="timer__head">
          <div className="timer__wheels">
            <Wheel
              label="TT"
              value={p2(shownH)}
              prev={p2((shownH + 1) % 24)}
              next={p2((shownH + 23) % 24)}
              prev2={p2((shownH + 2) % 24)}
              next2={p2((shownH + 22) % 24)}
              active={wheel === 0}
              onFocus={() => setWheel(0)}
              onStep={(d) => bump(0, d)}
            />
            <span className="timer__colon">:</span>
            <Wheel
              label="MM"
              value={p2(shownM)}
              prev={p2((shownM + 1) % 60)}
              next={p2((shownM + 59) % 60)}
              prev2={p2((shownM + 2) % 60)}
              next2={p2((shownM + 58) % 60)}
              active={wheel === 1}
              onFocus={() => setWheel(1)}
              onStep={(d) => bump(1, d)}
            />
          </div>
          <span className="timer__sub">{sub}</span>
        </div>

        {phase !== "idle" && startMin !== null ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-md)" }}>
            <div className="timer__line">
              <span className="timer__linelabel">
                {phase === "working" ? "Startet" : "Timer på jobb"}
              </span>
              <button
                type="button"
                className={`timer__linevalue${phase === "ending" && over > 0 ? " timer__linevalue--over" : ""}`}
                onClick={() => setSheet({
                  kind: "time",
                  field: phase === "working" ? "start" : "end",
                  h: Math.floor((phase === "working" ? startMin : endLive) / 60),
                  m: (phase === "working" ? startMin : endLive) % 60,
                })}
              >
                <span className="mono">
                  {phase === "working" ? hm(startMin) : dur(worked)}
                </span>
                <span className="timer__edit">endre</span>
              </button>
            </div>

            {phase === "ending" ? (
              <div className="timer__span">
                <span>Inn – ut</span>
                <span className="mono">{hm(startMin)} – {hm(endLive)}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        {phase === "idle" && logged.length > 0 ? (
          <>
            <div className="timer__loghead">
              <span>LOGG</span>
              <button type="button" onClick={() => setSheet({ kind: "clear" })}>TØM</button>
            </div>
            <div className="timer__list">
              {logged.map((t) => {
                const d = varighet(t.startedAtUtcMs, t.stoppedAtUtcMs ?? t.startedAtUtcMs);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className="timer__day"
                    onClick={() => setSheet({ kind: "detail", id: t.id })}
                  >
                    <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <span style={{ fontSize: 14.5, color: "var(--ink)" }}>
                        {dayLabel(t.startedAtUtcMs)}
                      </span>
                      <span className="mono" style={{ fontSize: 12.5, color: "var(--ink-dim)" }}>
                        {hm(minutesOfDay(t.startedAtUtcMs))} – {hm(minutesOfDay(t.stoppedAtUtcMs ?? 0))}
                      </span>
                    </span>
                    <span
                      className={`mono${d > NORMAL_DAY_MIN ? " timer__dayover" : ""}`}
                      style={{ fontSize: 21, fontWeight: 500 }}
                    >
                      {dur(d)}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div style={{ flex: 1 }} />
        )}

        {!sheetOpen ? (
          <>
            <button
              type="button"
              className="timer__action timer__action--running"
              disabled={laast}
              onClick={action}
            >
              {actionLabel}
            </button>
            {phase === "ending" ? (
              <button
                type="button"
                className="timer__secondary"
                // Flagget skal ikke overleve verdien sitt.
                onClick={() => { endEdited.current = false; setPendingEnd(null); }}
              >
                Jeg er på jobb likevel
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {sheet.kind === "time" ? (
        <TimeSheet
          sheet={sheet}
          onChange={setSheet}
          onCancel={() => setSheet({ kind: "none" })}
          onConfirm={(minutes) => {
            if (sheet.field === "end") {
              /*
               * Regi har valgt sluttiden selv.
               *
               * Uten dette flagget falt lagringen tilbake paa klokkeslettet
               * «Ferdig paa jobb» ble trykket, og valget forsvant i det dagen
               * ble avsluttet — en dag satt til aatte timer ble til de faa
               * minuttene mellom trykkene. Hjulet paa hovedflata satte
               * flagget; dette arket gjorde det ikke.
               */
              endEdited.current = true;
              setPendingEnd(minutes);
            } else if (open !== null) {
              startTimer(atMinutes(open.startedAtUtcMs, minutes), open.id);
            }
            setSheet({ kind: "none" });
          }}
          startMin={startMin}
          endMin={endLive}
        />
      ) : null}

      {sheet.kind === "overtime" ? (
        <div className="tsheet">
          <LiquidGlass className="tsheet__card" grade="top">
            <div className="tsheet__head">
              <span className="mono" style={{ color: "var(--state-warn)", letterSpacing: "0.12em", fontSize: 11 }}>
                OVERTID {dur(over)}
              </span>
              <span className="mono" style={{ fontSize: 12, color: "var(--ink-dim)" }}>
                {startMin === null ? "" : `${hm(startMin)} – ${hm(endLive)}`}
              </span>
            </div>
            <span style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink)" }}>
              Dagen er over 8 timer. Skriv kort hvorfor.
            </span>
            <textarea
              className="tsheet__textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Forsinket rigg, ventet på lys"
            />
            <div className="tsheet__row">
              <button type="button" onClick={() => setSheet({ kind: "none" })}>Avbryt</button>
              <button
                type="button"
                className={note.trim().length > 0 ? "is-ready" : ""}
                onClick={() => { if (note.trim().length > 0) saveDay(note.trim()); }}
              >
                Lagre dagen
              </button>
            </div>
          </LiquidGlass>
        </div>
      ) : null}

      {sheet.kind === "clear" ? (
        <div className="tsheet">
          <LiquidGlass className="tsheet__card" grade="top">
            <span style={{ fontWeight: 500, fontSize: 15, color: "var(--ink)" }}>Tømme loggen?</span>
            <span style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-dim)" }}>
              {logged.length === 1 ? "Den ene dagen i loggen" : `Alle ${logged.length} dagene i loggen`}
              {" "}forsvinner for godt. Ellers blir dagene liggende her.
            </span>
            <div className="tsheet__row">
              <button type="button" onClick={() => setSheet({ kind: "none" })}>Avbryt</button>
              <button
                type="button"
                style={{ color: "var(--state-warn)", fontWeight: 500 }}
                onClick={() => { clearTimeEntries(); setSheet({ kind: "none" }); }}
              >
                Tøm loggen
              </button>
            </div>
          </LiquidGlass>
        </div>
      ) : null}

      {detail !== null ? (
        <div className="tsheet">
          <LiquidGlass className="tsheet__card" grade="top">
            <div className="tsheet__head">
              <span>{dayLabel(detail.startedAtUtcMs)}</span>
              <span className="mono" style={{ fontSize: 26, fontWeight: 500, color: "var(--ink)" }}>
                {dur(varighet(detail.startedAtUtcMs, detail.stoppedAtUtcMs ?? detail.startedAtUtcMs))}
              </span>
            </div>
            <div className="tsheet__detail">
              <div><span>Inn</span><span className="mono">{hm(minutesOfDay(detail.startedAtUtcMs))}</span></div>
              <div><span>Ut</span><span className="mono">{hm(minutesOfDay(detail.stoppedAtUtcMs ?? 0))}</span></div>
              <div>
                <span>Over 8 timer</span>
                <span className="mono">
                  {dur(Math.max(0, varighet(
                    detail.startedAtUtcMs,
                    detail.stoppedAtUtcMs ?? detail.startedAtUtcMs,
                  ) - NORMAL_DAY_MIN))}
                </span>
              </div>
            </div>
            {detail.note.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                <span className="mono" style={{ fontSize: 11, letterSpacing: "0.12em", color: "var(--ink-dim)" }}>
                  BEGRUNNELSE
                </span>
                <span className="mono" style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink)" }}>
                  {detail.note}
                </span>
              </div>
            ) : null}
            <div className="tsheet__row" style={{ justifyContent: "space-between" }}>
              <button type="button" onClick={() => { deleteTimeEntry(detail.id); setSheet({ kind: "none" }); }}>
                Slett dagen
              </button>
              <button type="button" className="is-ready" onClick={() => setSheet({ kind: "none" })}>
                Lukk
              </button>
            </div>
          </LiquidGlass>
        </div>
      ) : null}
    </div>
  );
}

/** Ark for å sette tid inn eller tid ut med hjul. */
function TimeSheet({
  sheet, onChange, onCancel, onConfirm, startMin, endMin,
}: {
  sheet: Extract<Sheet, { kind: "time" }>;
  onChange(s: Sheet): void;
  onCancel(): void;
  onConfirm(minutes: number): void;
  startMin: number | null;
  endMin: number;
}): JSX.Element {
  const [wheel, setWheel] = useState<0 | 1>(1);
  const value = sheet.h * 60 + sheet.m;
  const isStart = sheet.field === "start";
  const candidate = isStart ? span(value, endMin) : span(startMin ?? value, value);

  return (
    <div className="tsheet">
      <LiquidGlass className="tsheet__card" grade="top">
        <div className="tsheet__head">
          <span>{isStart ? "Sett tid inn" : "Sett tid ut"}</span>
          <span className="mono" style={{ fontSize: 12.5, color: "var(--ink-dim)", fontWeight: 400 }}>
            Dagen blir <span style={{ color: "var(--ink)" }}>{dur(candidate)}</span>
          </span>
        </div>

        <div className="timer__wheels">
          <Wheel
            label="TT"
            size="small"
            value={p2(sheet.h)}
            prev={p2((sheet.h + 1) % 24)}
            next={p2((sheet.h + 23) % 24)}
            prev2={p2((sheet.h + 2) % 24)}
            next2={p2((sheet.h + 22) % 24)}
            active={wheel === 0}
            onFocus={() => setWheel(0)}
            onStep={(d) => onChange({ ...sheet, h: ((sheet.h + d) % 24 + 24) % 24 })}
          />
          <Wheel
            label="MM"
            size="small"
            value={p2(sheet.m)}
            prev={p2((sheet.m + 1) % 60)}
            next={p2((sheet.m + 59) % 60)}
            prev2={p2((sheet.m + 2) % 60)}
            next2={p2((sheet.m + 58) % 60)}
            active={wheel === 1}
            onFocus={() => setWheel(1)}
            onStep={(d) => onChange({ ...sheet, m: ((sheet.m + d) % 60 + 60) % 60 })}
          />
        </div>

        <div className="tsheet__note">
          <span style={{ color: "var(--ink)" }}>
            {isStart ? "Starten på arbeidsdagen" : "Slutten på arbeidsdagen"}
          </span>
          <span style={{ color: "var(--ink-dim)" }}>
            {isStart
              ? startMin === null ? "ikke satt" : hm(startMin)
              : hm(endMin)}
            {"  →  "}
            {hm(value)}
          </span>
        </div>

        <button type="button" className="tsheet__confirm" onClick={() => onConfirm(value)}>
          Bekreft
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{ minHeight: 44, fontSize: 13, color: "var(--ink-dim)" }}
        >
          Avbryt
        </button>
      </LiquidGlass>
    </div>
  );
}
